import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Fragment, memo, useCallback, useContext, useMemo } from 'react';
import {
  type SwimlaneEventListener,
  useSwimlaneInternalEventHandler,
} from '@/common/components/ui/swimlane/api';
import {
  CardVirtualizationContext,
  useSwimlaneRootProps,
  useSwimlaneSlot,
  useSwimlaneState,
} from '@/common/components/ui/swimlane/context';
import type { IHeader, TColumnID, TRowID } from '@/common/components/ui/swimlane/types';
import { buildCellId, parseCardId } from '@/common/components/ui/swimlane/utils';
import { CELL_GAP, IMPLICIT_ROW_ID } from '../../constants';
import { getCardSlotHeight } from '../../geometry';
import { useMemoizedValue } from '../../hooks/useMemoizedValue';
import { AddCardForm } from '../create/AddCardForm';
import { InlineAdd } from '../create/InlineAdd';
import { SortableCard } from './SortableCard';

type TCardsProps = {
  cellTop: number;
  columnHeader: IHeader;
  rowHeader?: IHeader;
};

const emptyCellStyle = { minWidth: 'inherit', minHeight: 'inherit' };

export const Cards = memo(({ cellTop, columnHeader, rowHeader, ...rest }: TCardsProps) => {
  const columnID: TColumnID = columnHeader.id;
  const rowID: TRowID = rowHeader?.id ?? IMPLICIT_ROW_ID;
  const { readOnly, shouldHideInlineAdd, maxItemsPerCell } = useSwimlaneRootProps();
  const { component: SlotAddCardForm = AddCardForm, props: addCardFormSlotProps } =
    useSwimlaneSlot('addCardForm');
  const { getCellCardIDs, hasCardDraft, removeCardDraft, getCardHeight } = useSwimlaneState();
  const cellId = buildCellId({ rowID, columnID });
  const cardIDs = useMemoizedValue(getCellCardIDs(cellId));
  const cardIndexByID = useMemo(
    () => new Map(cardIDs.map((cardId, index) => [cardId, index])),
    [cardIDs],
  );
  const hasRoomForMoreCards = cardIDs.length < maxItemsPerCell;
  const hideInlineAdd = shouldHideInlineAdd || !hasRoomForMoreCards;
  const rowVirtualizer = useContext(CardVirtualizationContext);

  const getCardId = useCallback((index: number) => cardIDs[index] ?? '', [cardIDs]);

  const calculateVirtualItemHeight = useCallback(
    (index: number, cardHeight: number) => {
      const cardId = getCardId(index);
      return getCardSlotHeight({
        cardHeight,
        hasCellDraftAbove: !readOnly && index === 0 && hasCardDraft(cellId),
        hasCardDraftBelow: !readOnly && hasCardDraft(cardId),
      });
    },
    [cellId, getCardId, hasCardDraft, readOnly],
  );

  const estimateSize = useCallback(
    (index: number) => {
      const cardId = getCardId(index);
      const cardHeight = getCardHeight(cardId);

      return calculateVirtualItemHeight(index, cardHeight);
    },
    [calculateVirtualItemHeight, getCardHeight, getCardId],
  );

  const cardsVirtualizer = useVirtualizer({
    count: cardIDs.length,
    getScrollElement: () => rowVirtualizer?.scrollElement ?? null,
    getItemKey: getCardId,
    estimateSize,
    gap: CELL_GAP.value,
    overscan: 2,
    scrollMargin: cellTop,
    // Live scrollTop: the cached offset lags programmatic jumps, resetting the scroll.
    initialOffset: () => rowVirtualizer?.scrollElement?.scrollTop ?? 0,
  });

  const onCardHeightChange = useCallback<SwimlaneEventListener<'swimlaneCardHeightChange'>>(
    ({ changedCards }) => {
      if (changedCards.get(cellId)?.size) {
        for (const [cardId, cardHeight] of changedCards.get(cellId) || new Map()) {
          const index = cardIndexByID.get(cardId);
          if (typeof index === 'number') {
            cardsVirtualizer.resizeItem(index, calculateVirtualItemHeight(index, cardHeight));
          }
        }
      }
    },
    [calculateVirtualItemHeight, cardIndexByID, cardsVirtualizer, cellId],
  );
  useSwimlaneInternalEventHandler('swimlaneCardHeightChange', onCardHeightChange);

  const virtualCards = cardsVirtualizer.getVirtualItems();
  const cardIndices = virtualCards.map((v) => v.index);

  return (
    <SortableContext items={cardIDs} strategy={verticalListSortingStrategy}>
      <div
        className="flex flex-col items-center"
        data-testid="virtual-cards"
        style={
          !cardIDs.length
            ? emptyCellStyle
            : {
                rowGap: CELL_GAP.label,
                transform: `translateY(${(virtualCards[0]?.start ?? 0) - cardsVirtualizer.options.scrollMargin}px)`,
              }
        }
      >
        {!readOnly &&
          !cardIDs.length &&
          (hasCardDraft(cellId) ? (
            <SlotAddCardForm
              key={`card-draft-${cellId}`}
              columnHeader={columnHeader}
              rowHeader={rowHeader}
              index={0}
              onClose={() => removeCardDraft(cellId)}
              {...addCardFormSlotProps}
            />
          ) : (
            !hideInlineAdd && <InlineAdd rowID={rowID} columnID={columnID} variant="card" />
          ))}
        {cardIndices.map((index) => {
          const cardId = getCardId(index);
          const { itemID } = parseCardId(cardId);
          return (
            <Fragment key={cardId}>
              {!readOnly && index === 0 && hasCardDraft(cellId) && (
                <SlotAddCardForm
                  key={`card-draft-${cellId}`}
                  columnHeader={columnHeader}
                  rowHeader={rowHeader}
                  index={0}
                  onClose={() => removeCardDraft(cellId)}
                  {...addCardFormSlotProps}
                />
              )}
              <SortableCard cardId={cardId} {...rest} />
              {!readOnly &&
                (hasCardDraft(cardId) ? (
                  <SlotAddCardForm
                    key={`card-draft-${cardId}`}
                    columnHeader={columnHeader}
                    rowHeader={rowHeader}
                    index={index}
                    onClose={() => removeCardDraft(cardId)}
                    {...addCardFormSlotProps}
                  />
                ) : (
                  !hideInlineAdd && (
                    <InlineAdd
                      rowID={rowID}
                      columnID={columnID}
                      prevItemID={itemID}
                      variant="divider"
                    />
                  )
                ))}
            </Fragment>
          );
        })}
      </div>
    </SortableContext>
  );
});
